import React, { useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { DemandCarliChat } from './DemandCarliChat';
import { DemandPlatformConnections } from './DemandPlatformConnections';
import { DemandOpportunity } from './DemandOpportunity';
import { DemandAdStudio } from './DemandAdStudio';
import { DemandCampaigns } from './DemandCampaigns';
import { DemandSocial } from './DemandSocial';
import { DemandBriefing } from './DemandBriefing';
import { DemandWorkRail } from './DemandWorkRail';
import { DemandSnapshots } from './DemandSnapshots';
import { demandMetrics } from '../data/demandData';
type DemandScope = {
  label: string;
  detail: string;
};
const tabs = ['Demand HQ', 'Opportunities', 'Ad Studio', 'Campaigns', 'Social', 'Connections'] as const;
type DemandTab = (typeof tabs)[number];
function getDemandScope(selectedOwner: string): DemandScope {
  if (selectedOwner === 'Eddy') return {
    label: 'Eddy',
    detail: 'Eddy inventory and approved channel context'
  };
  if (selectedOwner === '1 People') return {
    label: '1 People',
    detail: '1 People inventory and approved channel context'
  };
  if (selectedOwner) return {
    label: selectedOwner,
    detail: 'Selected brand inventory and approved channel context'
  };
  return {
    label: 'All available brands',
    detail: 'Live inventory, campaign, and channel context across approved brands'
  };
}
export function DemandDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const scope = getDemandScope(selectedOwner);
  const [tab, setTab] = useState<DemandTab>('Demand HQ');
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const workRailRef = useRef<HTMLDivElement>(null);
  return <div className="mx-auto max-w-[1500px] px-5 py-6 sm:px-8 lg:py-8" aria-labelledby="demand-heading" data-id="element-838" data-mp="38p91rlj8qmw3">
      <h1 id="demand-heading" className="sr-only" data-id="element-839" data-mp="2zqxffvpeqb9g">Carli for Demand</h1>

      <div className="border-b border-ink/10" role="tablist" aria-label="Demand sections" data-id="element-840" data-mp="2zq5hie8c81g0">
        <div className="-mb-px flex gap-1 overflow-x-auto" data-id="element-841" data-mp="2uscchoirtdhb">
          {tabs.map(option => {
          const active = tab === option;
          return <button key={option} type="button" role="tab" aria-selected={active} onClick={() => setTab(option)} className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? 'border-[#BB5E40] text-[#94452E]' : 'border-transparent text-ink/55 hover:text-ink'}`} data-id="element-842" data-mp="2q3l3yhxrjb78">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-7" data-id="element-843" data-mp="2u2ty3nwptb3b">
        {tab === 'Demand HQ' && <>
            <section aria-label="Demand performance" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-844" data-mp="2z2evvv05fdeg">
              {demandMetrics.map(metric => <div key={metric.label} className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-845" data-mp="3rktzf582uyy0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-846" data-mp="1g4s2j800fjen">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-847" data-mp="1b70beg76w1bi">{metric.value}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink/50" data-id="element-848" data-mp="1pu3agrb5ui0y">{metric.detail}</p>
                </div>)}
            </section>

            <div className="mt-4" data-id="element-849" data-mp="x6fszl56s7ij">
              <DemandBriefing onSelectSignal={id => {
            if (id === 'approvals') setTab('Ad Studio');else if (id === 'attention') setTab('Campaigns');else workRailRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} data-id="element-850" data-mp="x5nyyer8mhud" />
            </div>

            <section aria-label="Carli collaboration workspace" className="mt-7" data-id="element-851" data-mp="127wsbc80vlx0">
              <button type="button" onClick={() => setWorkspaceOpen(open => !open)} aria-expanded={workspaceOpen} className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left ${workspaceOpen ? 'mb-3' : 'border border-ink/10 bg-paper shadow-sm'}`} data-id="element-852" data-mp="2hkhja94bh45r">
                <span className="flex items-center gap-2 text-sm font-semibold" data-id="element-853" data-mp="3hvap1vv59tl3">
                  <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
                WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-854" data-mp="3mudlwt0z0thz" />
                  Carli workspace
                  <span className="hidden text-xs font-normal text-ink/50 sm:inline" data-id="element-855" data-mp="23xaahmqhfnw7">· Conversations, campaign rooms, and team channels</span>
                </span>
                {workspaceOpen ? <ChevronDownIcon className="h-4 w-4 text-ink/45" data-id="element-856" data-mp="1tykszv2i05gh" /> : <ChevronRightIcon className="h-4 w-4 text-ink/45" data-id="element-857" data-mp="1oyyb38bq611c" />}
              </button>
              {workspaceOpen && <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]" data-id="element-858" data-mp="2mlf78a8q28li">
                  <DemandCarliChat scopeLabel={scope.label} scopeDetail={scope.detail} data-id="element-859" data-mp="2hmu7ebxdtqxl" />
                  <div ref={workRailRef} className="scroll-mt-6" data-id="element-860" data-mp="2cwdssohtrz9n">
                    <DemandWorkRail onViewProjects={() => setTab('Campaigns')} data-id="element-861" data-mp="2cukk73nnj4z0" />
                  </div>
                </div>}
            </section>

            <div className="mt-8" data-id="element-862" data-mp="35dzw7glavtk1">
              <DemandSnapshots onGoToOpportunities={() => setTab('Opportunities')} onGoToCampaigns={() => setTab('Campaigns')} onGoToSocial={() => setTab('Social')} data-id="element-863" data-mp="35f197qlvmq4e" />
            </div>
          </>}

        {tab === 'Opportunities' && <DemandOpportunity selectedOwner={selectedOwner} data-id="element-864" data-mp="13mzitjizldje" />}
        {tab === 'Ad Studio' && <DemandAdStudio data-id="element-865" data-mp="oyd1jc9subpj" />}
        {tab === 'Campaigns' && <DemandCampaigns selectedOwner={selectedOwner} data-id="element-866" data-mp="txppk0cepotp" />}
        {tab === 'Social' && <DemandSocial data-id="element-867" data-mp="tvmpenhjifb9" />}
        {tab === 'Connections' && <DemandPlatformConnections data-id="element-868" data-mp="fx522qvjr4tv" />}
      </div>
    </div>;
}